import { forwardRef, useContext } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/styles';
import { TimelineContext } from './Timeline';
import { useChildPosition } from '@page/core/hooks/dom/useChildPosition';
import { useMergedRefs } from '@page/core/hooks/common';

export type TimelineItemProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  /**Custom bullet element */
  bullet?: React.ReactNode;
  /** Title displayed next to the bullet */
  title?: React.ReactNode;
  /** Additional class names for the title */
  titleClassName?: string;
  children?: React.ReactNode;
};

export const TimelineItem = forwardRef<HTMLDivElement, TimelineItemProps>(
  ({ className, children, bullet: customBullet, title, titleClassName, ...props }, forwardedRef) => {
    const { bulletSize = 16 } = useContext(TimelineContext) ?? {};

    const { ref: refForChildPosition, isFirstItem, isLastItem } = useChildPosition();
    const ref = useMergedRefs(forwardedRef, refForChildPosition);

    const bullet = (
      <Slot className="size-4" style={{ width: bulletSize, height: bulletSize }}>
        {customBullet ?? <div className="rounded-full bg-border-secondary" />}
      </Slot>
    );

    return (
      <div ref={ref} className="flex gap-1 font-body-sm" {...props}>
        <div className="flex flex-col items-center">
          <div
            className={cn('w-[2px] h-1 shrink-0', {
              'bg-border-secondary': !isFirstItem,
            })}
          />
          <div className="bg-bg-sur-primary p-[2.5px]">{bullet}</div>
          {!isLastItem && <div className="w-[2px] h-full bg-border-secondary" />}
        </div>
        <div className="flex flex-col flex-1">
          {!!title && (
            <div className={cn('text-text-tertiary pt-1 pb-1.5 flex gap-1.5 items-center', titleClassName)}>
              {title}
            </div>
          )}
          <div className={cn('pb-4', className)}>{children}</div>
        </div>
      </div>
    );
  },
);

TimelineItem.displayName = 'Timeline.Item';
